import type { CaptionEmojiMark, CaptionKeyword, CaptionStyleConfig, SceneChunk } from "@/remotion/schemas"

// Caption word in times are floor(seconds * 1000) on both backend and frontend; allow float drift.
const TIME_TOLERANCE_MS = 5
const LOWER_THIRD_Y = 0.78
const CAPTION_BAND_HALF_HEIGHT = 0.07
const AUTO_CANDIDATES_Y = [LOWER_THIRD_Y, 0.86, 0.22]

export interface TimedCaptionWord {
  text: string
  timelineIn: number
}

export function isKeywordWord(timelineIn: number, keywords: CaptionKeyword[] | undefined): boolean {
  return (keywords ?? []).some(
    (keyword) =>
      timelineIn >= keyword.startMs - TIME_TOLERANCE_MS && timelineIn <= keyword.endMs + TIME_TOLERANCE_MS,
  )
}

const KEYWORD_COLOR = "#FFFD03"
const KEYWORD_FALLBACK_COLORS = [KEYWORD_COLOR, "#04F827", "#00D4FF"]

/**
 * The AI keyword colour, never the active-word colour (or the plain word colour): a keyword lit like
 * the active word reads as two words highlighted at once.
 */
export function resolveKeywordColor(style: Pick<CaptionStyleConfig, "keywordColor" | "colors">): string {
  const taken = [style.colors.primary, style.colors.default].map((color) => color.toLowerCase())
  return [style.keywordColor ?? KEYWORD_COLOR, style.colors.secondary, ...KEYWORD_FALLBACK_COLORS]
    .find((color) => !!color && !taken.includes(color.toLowerCase())) ?? KEYWORD_COLOR
}

/** The first suggested emoji whose word falls inside this caption chunk. */
export function getChunkEmoji(
  chunk: TimedCaptionWord[],
  emojis: CaptionEmojiMark[] | undefined,
): string | null {
  if (chunk.length === 0) return null
  const startMs = chunk[0].timelineIn - TIME_TOLERANCE_MS
  const endMs = chunk[chunk.length - 1].timelineIn + TIME_TOLERANCE_MS
  return (emojis ?? []).find((mark) => mark.startMs >= startMs && mark.startMs <= endMs)?.emoji ?? null
}

/** Greedy word-wrap line count for words at a given line length in characters. */
export function countCaptionLines(texts: string[], charsPerLine: number): number {
  let lines = 0
  let lineLength = 0
  for (const text of texts) {
    if (lineLength === 0) {
      lines += 1
      lineLength = text.length
    } else if (lineLength + 1 + text.length <= charsPerLine) {
      lineLength += 1 + text.length
    } else {
      lines += 1
      lineLength = text.length
    }
  }
  return lines
}

/** Rough characters per line for a caption box; bold uppercase glyphs run wide. */
export function estimateCharsPerLine(widthPx: number, fontSizePx: number, uppercase: boolean): number {
  return Math.max(1, Math.floor(widthPx / (fontSizePx * (uppercase ? 0.72 : 0.6))))
}

/** The active caption word grows by this much. */
export const ACTIVE_WORD_SCALE = 1.03

/**
 * Horizontal gap (px) between caption words, in the caption's own font size: a word space, the
 * stroke both neighbours paint outside their glyphs, and how far a scaled-up active word grows.
 */
export function captionWordGapPx(
  fontSizePx: number,
  strokeWidthPx: number,
  longestWordChars: number,
  uppercase: boolean,
): number {
  const longestWordPx = longestWordChars * fontSizePx * (uppercase ? 0.72 : 0.6)
  return fontSizePx * 0.55 + strokeWidthPx + (longestWordPx * (ACTIVE_WORD_SCALE - 1)) / 2
}

/** A caption y (0..1, centre) clear of the scene's faces, else the lower third. */
export function getAutoCaptionY(chunk: Pick<SceneChunk, "mode" | "faces"> | null | undefined): number {
  if (!chunk) return LOWER_THIRD_Y
  // Split layouts stack panels top/bottom: the seam sits between faces.
  if (chunk.mode === "dual" || chunk.mode === "triple" || chunk.mode === "quad") return 0.5
  if (chunk.mode !== "single" || !chunk.faces?.length) return LOWER_THIRD_Y
  // Single mode crops full source height, so source face y maps straight to output y.
  const faces = chunk.faces
  const clear = AUTO_CANDIDATES_Y.find((y) =>
    faces.every(
      (face) =>
        face.y_pct + face.h_pct / 2 < y - CAPTION_BAND_HALF_HEIGHT ||
        face.y_pct - face.h_pct / 2 > y + CAPTION_BAND_HALF_HEIGHT,
    ),
  )
  return clear ?? LOWER_THIRD_Y
}

/** Caption centre as fractions of the frame for the style's position mode. */
export function resolveCaptionCenter(
  style: Pick<CaptionStyleConfig, "position" | "positionOffset" | "positionX" | "positionY">,
  chunk?: Pick<SceneChunk, "mode" | "faces"> | null,
): { x: number; y: number } {
  switch (style.position) {
    case "top":
      return { x: 0.5, y: style.positionOffset / 100 }
    case "middle":
      return { x: 0.5, y: 0.5 }
    case "auto":
      return { x: 0.5, y: getAutoCaptionY(chunk) }
    case "custom":
      return { x: style.positionX ?? 0.5, y: style.positionY ?? LOWER_THIRD_Y }
    default:
      return { x: 0.5, y: 1 - style.positionOffset / 100 }
  }
}
